import { computed, observable } from 'mobx';
import { clone } from 'underscore';

import { PhotoPageService } from '../Services/PhotoPageService';
import { PhotoService, TagType } from '../Services/PhotoService';
import { IUser } from '../_interfaces/IUser';

export interface IUserPhoto {
    created_at: string;
    id: number;
    url: string;
    user_id: number;
    description: IUploadedPhoto;
    tags?: Partial<Record<keyof typeof TagType, string>>;
    rotate_degrees: number;
    thumbnail_url: string;
    index: number;
    user_info: IUser;
}

export interface IUserPhotosResponse {
    meta: {
        count: number;
        page: number;
        pageSize: number;
        total: number;
        verbose: boolean;
    };
    items: Array<IUserPhoto>;
}

export interface IGetAllPhotosForCurrentUserMeta {
    page_size?: number;
    page: number;
    sort: string;
}

export interface ICheckForDuplicatesData {
    fileNames: Array<string>;
}



export interface IUploadedPhoto {
    image?: any;
    location: string;
    date: string;
    rotateDegrees: number;
    filename: string;
    error?: string;
}

export class UserPhotoStore {
    @observable
    private _possibleDupes: Array<IUserPhoto> = [];

    @observable
    private _userPhotos: Array<IUserPhoto> = [];

    @observable
    private _initialPhotosAreLoaded = false;
    private photoService: PhotoService;
    private photoPageService: PhotoPageService;

    @observable
    private _checkingForDupes = false;

    @observable
    private _checkingForDupesError = false;


    @observable
    private _loadingPhotos = false;

    @observable
    private _loadingPhotosError = false;

    @observable
    private _allPhotosLoaded = false;

    @computed
    public get allPhotosLoaded(): boolean {
        return this._allPhotosLoaded;
    }

    @observable
    private _photosPageScrollTo = 0;

    @computed
    public get photosPageScrollTo(): number {
        return this._photosPageScrollTo;
    }

    @observable
    private _photosPageSort = 'Newest';

    @computed
    public get photosPageSort(): string {
        return this._photosPageSort;
    }

    private initialGetAllPhotosForCurrentUserMeta = {
        page_size: 1000,
        page: 0,
        sort: 'DESC',
    };

    @observable
    private _getAllPhotosForCurrentUserMeta: IGetAllPhotosForCurrentUserMeta = this.initialGetAllPhotosForCurrentUserMeta;

    @observable
    private _cheeckForDuplicatesData: ICheckForDuplicatesData = {
        fileNames: [],
    };

    @computed
    public get getAllPhotosForCurrentUserMeta(): IGetAllPhotosForCurrentUserMeta {
        return this._getAllPhotosForCurrentUserMeta;
    }

    @computed
    public get userPhotos(): Array<IUserPhoto> {
        // assert(this._userPhotos, 'We should be waiting for .initialPhotosAreLoaded before asking for .userPhotos');
        return this._userPhotos || [];
    }

    @computed
    public get possibleDupes(): Array<IUserPhoto> {
        // assert(this._userPhotos, 'We should be waiting for .initialPhotosAreLoaded before asking for .userPhotos');
        return this._possibleDupes || [];
    }

    @computed
    public get initialPhotosAreLoaded(): boolean {
        return this._initialPhotosAreLoaded;
    }

    @computed
    public get loadingPhotos(): boolean {
        return this._loadingPhotos;
    }

    @computed
    public get loadingPhotosError(): boolean {
        return this._loadingPhotosError;
    }

    @computed
    public get checkingForDupes(): boolean {
        return this._checkingForDupes;
    }

    @computed
    public get checkingForDupesError(): boolean {
        return this._checkingForDupesError;
    }

    public constructor(token: string) {
        this.photoService = new PhotoService(token);
        this.photoPageService = new PhotoPageService(token);
        // this.getAllPhotosFromServer();
    }

    public setGetAllPhotosForCurrentUserMeta(meta: IGetAllPhotosForCurrentUserMeta) {
        if (meta.page === 0) {
            this._allPhotosLoaded = false;
        }
        const clonedMeta = clone(this._getAllPhotosForCurrentUserMeta);
        this._getAllPhotosForCurrentUserMeta = {...clonedMeta, ...meta};
        this._userPhotos = [];
        this.getAllPhotosFromServer();

    }

    public setPhotosPageScrollTo(scrollto: number) {
        this._photosPageScrollTo = scrollto;
    }

    public setPhotosPageSort(sort: string) {
        this._photosPageSort = sort;
    }

    public clearUserPhotos() {
        this._initialPhotosAreLoaded = false;
        this._allPhotosLoaded = false;
        const clonedMeta = clone(this._getAllPhotosForCurrentUserMeta);
        clonedMeta.page = 0;
        this._getAllPhotosForCurrentUserMeta = clonedMeta;
        this._userPhotos = [];
    }

    public async checkForDuplicatePhotos(uploadedFiles: Array<IUploadedPhoto>): Promise<void> {

        this._cheeckForDuplicatesData.fileNames = uploadedFiles.map(file => file.filename);

        return this.photoPageService.checkForDuplicates(this._cheeckForDuplicatesData).then(async response => {

            this._checkingForDupesError = false;
            this._checkingForDupes = true;

            const possibleDupes = [] as Array<IUserPhoto>;

            await Promise.all(
                response.map(async img => {
                    img.description = {
                        location: img.tags ? (img.tags as any)[TagType.LOCATION] : '',
                        filename: img.tags ? (img.tags as any)[TagType.FILENAME] : '',
                        date: img.tags ? (img.tags as any)[TagType.DATE] : '',
                        rotateDegrees: img.rotate_degrees
                    };
                    const matchIndex = possibleDupes.findIndex(i => i.description.filename === img.description.filename);
                    if (matchIndex === -1) {
                        possibleDupes.push(img);
                    }
                    Promise.resolve();
                })
            );
            this._possibleDupes = possibleDupes;
            this._checkingForDupes = false;
            return Promise.resolve();
        }).catch(err => {
            console.log('error checking for dupes');
            console.log('err', err);
            this._checkingForDupes = false;
            this._checkingForDupesError = true;
        });

    }

    public async getAllPhotosFromServer(hideSpinner?: boolean): Promise<void> {

        if (!hideSpinner) {
            this._loadingPhotos = true;
        }
        this._loadingPhotosError = false;

        return this.photoPageService.getAllPhotosForCurrentUser(this._getAllPhotosForCurrentUserMeta).then(async response => {

            const newPhotos = [] as Array<IUserPhoto>;
            await Promise.all(
                response.items.map(async img => {
                    img.description = {
                        location: img.tags ? (img.tags as any)[TagType.LOCATION] : '',
                        filename: img.tags ? (img.tags as any)[TagType.FILENAME] : '',
                        date: img.tags ? (img.tags as any)[TagType.DATE] : '',
                        rotateDegrees: img.rotate_degrees
                    };

                    newPhotos.push(img);
                    Promise.resolve();
                })
            );
            this._userPhotos = this._userPhotos.concat(newPhotos);

            const clonedMeta = clone(this._getAllPhotosForCurrentUserMeta);
            clonedMeta.page++;
            this._getAllPhotosForCurrentUserMeta = clonedMeta;

            if (this._userPhotos.length >= response.meta.total) {
                this._allPhotosLoaded = true;
            }
            // this._userPhotos = photos;
            this._initialPhotosAreLoaded = true;
            this._loadingPhotos = false;
            return Promise.resolve();
        }).catch(err => {
            console.log('error fetching photos');
            console.log('err', err);
            this._loadingPhotos = false;
            this._loadingPhotosError = true;
        });
    }

    public uploadPhoto(photo: IUploadedPhoto, thumbnail: any): Promise<IUserPhoto> {
        const objectForServer = {
            image: photo.image,
            tags: JSON.stringify({
                [TagType.DATE]: photo.date || '',
                [TagType.LOCATION]: photo.location || '',
                [TagType.FILENAME]: photo.filename || ''
            }),
            rotate_degrees: photo.rotateDegrees,
            image_thumbnail: thumbnail.image
        } as any;

        return this.photoService.uploadPhoto(objectForServer);
    }

    public updatePhoto(photo: IUserPhoto, rotatedPhoto?: Blob, rotatedThumbnail?: Blob): Promise<void> {
        if (this._userPhotos) {
            const indexToUpdate = this._userPhotos!.findIndex(p => p.id === photo.id);
            this._userPhotos![indexToUpdate] = photo;
        }
        return this.photoService.updatePhoto(photo, rotatedPhoto, rotatedThumbnail);
    }

    public deletePhoto(id: number): Promise<boolean> {
        const photos = [...this._userPhotos!];
        const removeIndex = photos.findIndex(p => p.id === id);
        photos.splice(removeIndex, 1);
        this._userPhotos = photos;
        return this.photoService
            .removePhoto(id)
            .then(() => {
                return true;
            })
            .catch(response => {
                console.error(response);
                return false;
            });
    }
}



// WEBPACK FOOTER //
// ./src/Stores/UserPhotoStore.ts